import useDappConfig from "./useDappConfig";
import { AGETH, RSETH, WAGETH, WRSETH } from "@config/contractsConfig";
import { BW_VARIANT, TBW_VARIANT } from "@components/BridgeWrapV2/types";
import { arbitrum, mainnet } from "viem/chains";
import {
  ETHEREUM_CHAIN_ID,
  bridgeDestinationChainIds,
  bridgeSourceChainIds,
} from "@constants/bridgeRoutes";

type ChainOption = { id: number; title: string };

const toOption = (chain: { id: number; name: string }): ChainOption =>
  chain.name.toLowerCase().includes("manta")
    ? { id: chain.id, title: chain.name.split(" ").slice(0, 2).join(" ") }
    : { id: chain.id, title: chain.name };

const useBridgeWrapConfig = (variant: TBW_VARIANT) => {
  const { swapEnabledChains, vaultSwapEnabledChains } = useDappConfig();
  const isAgETH = variant === BW_VARIANT.AGETH;

  // agETH keeps its own chain set and its own (Squid) links — untouched here.
  const agEthChains = [mainnet, ...vaultSwapEnabledChains];

  // rsETH selectability is derived from the bridge route table rather than a
  // flat list, because the routes are directional: Optimism can be a source
  // (exit to Ethereum) but never a destination, since bridging into Optimism
  // is closed.
  //
  // Arbitrum is listed explicitly because it has no `wrsETH`, so it is absent
  // from `swapEnabledChains` (which derives from `swappingContractsConfig`).
  // Without it the route table could mark Arbitrum bridgeable while the chain
  // never reached the selector at all.
  const rsEthChains = [mainnet, arbitrum, ...swapEnabledChains];
  const sourceIds = new Set([ETHEREUM_CHAIN_ID, ...bridgeSourceChainIds()]);
  const destinationIds = new Set([
    ETHEREUM_CHAIN_ID,
    ...bridgeDestinationChainIds(),
  ]);

  const config = {
    assets: [
      { id: RSETH, title: RSETH },
      { id: WRSETH, title: WRSETH },
      { id: WAGETH, title: WAGETH },
      { id: AGETH, title: AGETH },
    ],
    sourceChains: (isAgETH
      ? agEthChains
      : rsEthChains.filter((chain) => sourceIds.has(chain.id))
    ).map(toOption),
    destinationChains: (isAgETH
      ? agEthChains
      : rsEthChains.filter((chain) => destinationIds.has(chain.id))
    ).map(toOption),
  };

  return {
    ...config,
  };
};

export default useBridgeWrapConfig;
